import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  User,
  Mail,
  Phone,
  Briefcase,
  Link2,
  FileText,
  Send,
  CheckCircle2,
} from "lucide-react";

// Same theme tokens as the rest of the site.
const theme = {
  ink: "#221619",
  inkMuted: "#6B6265",
  accent: "#E63946",
  accentDeep: "#B22234",
  gold: "#D9A441",
  border: "#E6DFDC",
  surface: "#FBF8F6",
  surfaceAlt: "#F2ECE9",
  heroBg: "#1A0E10",
  darkBand: "#221619",
  darkBandDeep: "#160B0D",
};

const inputBase =
  "w-full pl-12 pr-4 py-4 rounded-xl bg-white/[0.04] border text-white placeholder-white/30 outline-none transition-colors duration-200";

export default function Apply() {
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");

    const formData = {
      fullName: e.target.fullName.value,
      email: e.target.email.value,
      phone: e.target.phone.value,
      position: e.target.position.value,
      resume: e.target.resume.value,
      coverLetter: e.target.coverLetter.value,
    };

    try {
      const response = await axios.post(
        "http://localhost:5000/api/applications",
        formData
      );
      console.log(response.data);
      setStatus("success");
      e.target.reset();
    } catch (error) {
      console.error(error);
      setStatus("error");
    }
  };

  return (
    <div
      className="max-w-3xl mx-auto p-10 rounded-3xl shadow-2xl relative overflow-hidden"
      style={{
        background: `linear-gradient(160deg, ${theme.darkBand} 0%, ${theme.darkBandDeep} 100%)`,
        border: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      {/* ambient glow, consistent with the site's dark-band pattern */}
      <div
        className="pointer-events-none absolute -top-16 -right-10 w-64 h-64 rounded-full blur-3xl opacity-20"
        style={{ backgroundColor: theme.accent }}
      />

      <div className="relative">
        <p
          className="inline-flex items-center gap-1.5 font-semibold uppercase tracking-wider text-xs mb-3"
          style={{ color: "#FF8A93" }}
        >
          <Briefcase size={13} />
          Careers
        </p>
        <h2 className="text-3xl font-bold text-white mb-1">Apply for a Job</h2>
        <p className="text-white/50 text-sm mb-8">
          Tell us a bit about yourself and we'll get back to you.
        </p>

        {status === "success" ? (
          <div className="flex flex-col items-center text-center py-12">
            <CheckCircle2 size={48} style={{ color: theme.accent }} />
            <h3 className="text-white text-xl font-semibold mt-4">
              Application submitted
            </h3>
            <p className="text-white/50 text-sm mt-2">
              Thanks for applying — we'll be in touch soon.
            </p>
            <button
              onClick={() => setStatus("idle")}
              className="mt-6 px-6 py-2.5 rounded-md text-white font-semibold hover:brightness-110 transition-all"
              style={{ backgroundColor: theme.accent }}
            >
              Submit another application
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <FieldWrap icon={User}>
              <input
                type="text"
                name="fullName"
                placeholder="Full Name"
                className={inputBase}
                style={{ borderColor: "rgba(255,255,255,0.1)" }}
                onFocus={(e) => (e.target.style.borderColor = theme.accent)}
                onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.1)")}
                required
              />
            </FieldWrap>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FieldWrap icon={Mail}>
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  className={inputBase}
                  style={{ borderColor: "rgba(255,255,255,0.1)" }}
                  onFocus={(e) => (e.target.style.borderColor = theme.accent)}
                  onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.1)")}
                  required
                />
              </FieldWrap>

              <FieldWrap icon={Phone}>
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number"
                  className={inputBase}
                  style={{ borderColor: "rgba(255,255,255,0.1)" }}
                  onFocus={(e) => (e.target.style.borderColor = theme.accent)}
                  onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.1)")}
                  required
                />
              </FieldWrap>
            </div>

          <FieldWrap icon={Briefcase}>
  <select
    name="position"
    className={`${inputBase} appearance-none`}
    style={{
      borderColor: "rgba(255,255,255,0.1)",
      colorScheme: "dark",
      backgroundColor: "#221619",
    }}
    onFocus={(e) => (e.target.style.borderColor = theme.accent)}
    onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.1)")}
    required
    defaultValue=""
  >
    <option value="" disabled style={{ backgroundColor: "#221619", color: "#ffffff80" }}>
      Select Position
    </option>
    <option value="frontend" style={{ backgroundColor: "#221619", color: "#fff" }}>
      Frontend Developer
    </option>
    <option value="backend" style={{ backgroundColor: "#221619", color: "#fff" }}>
      Backend Developer
    </option>
    <option value="bd" style={{ backgroundColor: "#221619", color: "#fff" }}>
      Business Development Executive
    </option>
  </select>
</FieldWrap>

            <FieldWrap icon={Link2}>
              <input
                type="url"
                name="resume"
                placeholder="Link to your resume (Google Drive / Dropbox)"
                className={inputBase}
                style={{ borderColor: "rgba(255,255,255,0.1)" }}
                onFocus={(e) => (e.target.style.borderColor = theme.accent)}
                onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.1)")}
              />
            </FieldWrap>

            <div className="relative">
              <FileText
                size={18}
                className="absolute left-4 top-4 text-white/30"
              />
              <textarea
                name="coverLetter"
                placeholder="Cover Letter / Message"
                rows="5"
                className={`${inputBase} resize-none`}
                style={{ borderColor: "rgba(255,255,255,0.1)" }}
                onFocus={(e) => (e.target.style.borderColor = theme.accent)}
                onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.1)")}
              ></textarea>
            </div>

            <motion.button
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={status === "submitting"}
              className="w-full flex items-center justify-center gap-2 text-white font-semibold py-4 rounded-md hover:brightness-110 transition-all disabled:opacity-60"
              style={{ backgroundColor: theme.accent }}
            >
              {status === "submitting" ? (
                "Submitting..."
              ) : (
                <>
                  Submit Application
                  <Send size={16} />
                </>
              )}
            </motion.button>

            {status === "error" && (
              <p className="text-center text-sm" style={{ color: theme.accent }}>
                Something went wrong — please try again.
              </p>
            )}
          </form>
        )}
      </div>
    </div>
  );
}

function FieldWrap({ icon: Icon, children }) {
  return (
    <div className="relative">
      <Icon size={18} className="absolute left-4 top-1/2 -translate-t-1/2 -translate-y-1/2 text-white/30" />
      {children}
    </div>
  );
}